import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FileText,
  PencilLine,
  ReceiptText,
  RotateCcw,
  SlidersHorizontal,
  PackageOpen,
  CheckCircle2,
  ListOrdered
} from "lucide-react";
import api from "../api/api";
import { useDialog } from "../components/DialogProvider";
import SearchBar from "../components/SearchBar";
import { formatDateTime } from "../utils/datetime";
import { useAuth } from "../auth/AuthProvider";

function toneBadgeClass(kind, value) {
  const v = String(value || "").trim();

  if (kind === "tujuan") {
    switch (v) {
      case "Defect":
        return "bg-rose-50 text-rose-700 ring-rose-600/10";
      case "Sampel":
        return "bg-violet-50 text-violet-700 ring-violet-600/10";
      case "Vendor":
        return "bg-emerald-50 text-emerald-700 ring-emerald-600/10";
      case "E-Commerce":
        return "bg-sky-50 text-sky-700 ring-sky-600/10";
      case "Manual":
      default:
        return "bg-zinc-50 text-zinc-700 ring-zinc-600/10";
    }
  }

  if (kind === "source") {
    switch (v) {
      case "Shopee":
      case "Tokopedia":
        return "bg-amber-50 text-amber-700 ring-amber-600/10";
      case "Spare":
      case "Manual":
      default:
        return "bg-zinc-50 text-zinc-700 ring-zinc-600/10";
    }
  }

  return "bg-zinc-50 text-zinc-700 ring-zinc-600/10";
}

function ReceiptTable({ title, rows }) {
  if (!rows || rows.length === 0) return null;

  return (
    <div className="mt-4 rounded-md border border-zinc-200 bg-white">
      <table className="min-w-full text-[13px]">
        <thead className="border-b border-zinc-200 bg-zinc-50/50">
          <tr>
            <th className="px-4 py-2.5 text-left font-semibold text-zinc-900">{title}</th>
            <th className="px-4 py-2.5 text-right font-semibold text-zinc-900">Qty</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {rows.map((c) => (
            <tr key={`${c.type || title}-${c.kode_barang || c.product_name || "-"}-${c.qty}`} className="hover:bg-zinc-50/50 transition-colors">
              <td className="px-4 py-2.5">
                <div className="font-medium text-zinc-900">{c.product_name || c.kode_barang || "-"}</div>
                {c.kode_barang ? (
                  <div className="font-mono text-[11px] text-zinc-500 mt-0.5">{c.kode_barang}</div>
                ) : null}
              </td>
              <td className="px-4 py-2.5 text-right font-semibold text-zinc-900">{c.qty}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function orderLabel(order) {
  return order?.order_number || order?.order_code || `#${order?.id || "-"}`;
}

function EditResiContent({ order, valueRef }) {
  const [value, setValue] = useState(String(order?.resi_number || ""));

  useEffect(() => {
    if (valueRef) valueRef.current = value;
  }, [value, valueRef]);

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4 text-[13px] text-zinc-700">
        <div className="font-semibold text-zinc-900 mb-2">Ubah nomor resi order</div>
        <div className="grid grid-cols-[80px_1fr] gap-1">
          <div className="text-zinc-500">Order</div>
          <div className="font-medium text-zinc-900">{orderLabel(order)}</div>
          <div className="text-zinc-500">Resi saat ini</div>
          <div className="font-mono">{order?.resi_number || "-"}</div>
          {order?.created_at && (
            <>
              <div className="text-zinc-500">Tanggal</div>
              <div>{formatDateTime(order.created_at) || "-"}</div>
            </>
          )}
        </div>
      </div>

      <div>
        <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Nomor Resi</label>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm"
          placeholder="Contoh: JNE-123456"
          autoFocus
        />
        <div className="mt-1.5 text-[11px] text-zinc-500">Opsional. Kosongkan jika ingin menghapus resi.</div>
      </div>
    </div>
  );
}

function RefundContent({ order, reasonRef }) {
  const [reason, setReason] = useState("");
  const totals = useMemo(() => {
    const items = order?.items || {};
    const rows = [
      ...(Array.isArray(items.PRODUCT) ? items.PRODUCT : []),
      ...(Array.isArray(items.BOM) ? items.BOM : []),
      ...(Array.isArray(items.BONUS) ? items.BONUS : []),
    ];
    return {
      totalLines: rows.length,
      totalQty: rows.reduce((sum, row) => sum + (Number(row?.qty) || 0), 0),
    };
  }, [order]);

  useEffect(() => {
    if (reasonRef) reasonRef.current = reason;
  }, [reason, reasonRef]);

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-[13px] text-amber-800">
        <div className="font-semibold text-amber-900 mb-2">Konfirmasi refund order</div>
        <div className="grid grid-cols-[80px_1fr] gap-1">
          <div className="text-amber-700">Order</div>
          <div className="font-medium">{orderLabel(order)}</div>
          <div className="text-amber-700">Item</div>
          <div>{totals.totalLines} baris | {totals.totalQty} qty</div>
        </div>
        <div className="mt-2 pt-2 border-t border-amber-200/50 text-[11px] text-amber-700">
          Semua item pada transaksi ini akan dikembalikan ke stok, lalu order dihapus dari histori.
        </div>
      </div>

      <div>
        <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Alasan refund</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm min-h-[100px]"
          placeholder="Contoh: salah input qty, transaksi dibatalkan customer, double entry, dll."
          autoFocus
        />
        <div className="mt-1.5 text-[11px] text-zinc-500">Wajib diisi. Alasan ini akan tercatat di Activity Log.</div>
      </div>
    </div>
  );
}

function History() {
  const dialog = useDialog();
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [q, setQ] = useState(() => String(searchParams.get("q") || ""));
  const [exactDate, setExactDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [tujuanFilter, setTujuanFilter] = useState("ALL");
  const [txnTypeFilter, setTxnTypeFilter] = useState("ALL");
  const debouncedQ = useDebouncedValue(q, 350);
  const cardRefs = useRef(new Map());
  const [highlightOrderId, setHighlightOrderId] = useState(0);

  const [exporting, setExporting] = useState(false);

  const computeTotals = useCallback((order) => {
    const items = order?.items || {};
    const productRows = Array.isArray(items.PRODUCT) ? items.PRODUCT : [];
    const bomRows = Array.isArray(items.BOM) ? items.BOM : [];
    const bonusRows = Array.isArray(items.BONUS) ? items.BONUS : [];

    const allRows = [...productRows, ...bomRows, ...bonusRows];
    const totalLines = allRows.length;
    const totalQty = allRows.reduce((sum, r) => sum + (Number(r?.qty) || 0), 0);

    return { totalLines, totalQty };
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/orders?limit=100");
      setHistory(res.data || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Gagal mengambil history");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSearch = useCallback(async (query) => {
    const qq = String(query || "").trim();
    if (!qq) {
      await fetchHistory();
      return;
    }

    try {
      setLoading(true);
      const res = await api.get(`/api/orders/search?q=${encodeURIComponent(qq)}&limit=100`);
      setHistory(res.data || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Gagal mencari order");
    } finally {
      setLoading(false);
    }
  }, [fetchHistory]);

  useEffect(() => {
    fetchSearch(debouncedQ);
  }, [fetchSearch, debouncedQ]);

  const openEditResi = useCallback(async (order) => {
    if (!order?.id) return;

    const valueRef = { current: String(order?.resi_number || "") };
    const ok = await dialog.custom(
      <EditResiContent order={order} valueRef={valueRef} />,
      { title: "Edit Resi", confirmText: "Simpan", cancelText: "Batal" }
    );
    if (!ok) return;

    const nextVal = String(valueRef.current || "").trim();

    try {
      await dialog.toastPromise(
        api.patch(`/api/orders/${order.id}/resi`, { resi_number: nextVal }),
        {
          loading: "Menyimpan resi...",
          success: "Resi tersimpan.",
          error: (e2) => e2?.response?.data?.message || e2.message || "Gagal menyimpan resi.",
        },
        { successTitle: "Berhasil", errorTitle: "Gagal" }
      );

      await fetchSearch(debouncedQ);
    } catch {
      // Error detail sudah ditampilkan oleh toastPromise.
    }
  }, [dialog, fetchSearch, debouncedQ]);

  const openRefund = useCallback(async (order) => {
    if (!order?.id) return;

    // Loop sampai alasan diisi atau user batal
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const reasonRef = { current: "" };
      const ok = await dialog.custom(
        <RefundContent order={order} reasonRef={reasonRef} />,
        { title: "Refund Order", confirmText: "Refund & Hapus", cancelText: "Batal" }
      );
      if (!ok) return;

      const reason = String(reasonRef.current || "").trim();
      if (!reason) {
        await dialog.alert("Alasan refund wajib diisi.", { title: "Wajib diisi" });
        continue;
      }

      try {
        await dialog.toastPromise(
          api.post(`/api/orders/${order.id}/refund`, { reason }),
          {
            loading: "Memproses refund...",
            success: "Refund berhasil. Stok dikembalikan.",
            error: (e2) => e2?.response?.data?.message || e2.message || "Gagal refund order.",
          },
          { successTitle: "Berhasil", errorTitle: "Gagal" }
        );

        await fetchSearch(debouncedQ);
      } catch {
        // Error detail sudah ditampilkan oleh toastPromise.
      }
      return;
    }
  }, [dialog, fetchSearch, debouncedQ]);

  const tujuanOptions = useMemo(() => {
    const set = new Set();
    (history || []).forEach((o) => {
      const tujuan = String(o?.tujuan || "").trim();
      if (tujuan) set.add(tujuan);
    });
    return ["ALL", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [history]);

  const filteredHistory = useMemo(() => {
    const getDateKey = (v) => {
      const s = String(v || "").trim();
      if (!s) return "";
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return "";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    return (history || []).filter((o) => {
      const d = getDateKey(o?.created_at);
      const tujuan = String(o?.tujuan || "").trim();
      const source = String(o?.source || "").trim();

      if (exactDate && d !== exactDate) return false;
      if (!exactDate && fromDate && d && d < fromDate) return false;
      if (!exactDate && toDate && d && d > toDate) return false;

      if (tujuanFilter !== "ALL" && tujuan !== tujuanFilter) return false;

      if (txnTypeFilter === "ECOMMERCE") {
        const ecommerce = tujuan === "E-Commerce" || source === "Shopee" || source === "Tokopedia";
        if (!ecommerce) return false;
      }

      if (txnTypeFilter === "MANUAL") {
        const manual = source === "Manual" || source === "Spare" || tujuan === "Manual";
        if (!manual) return false;
      }

      return true;
    });
  }, [history, exactDate, fromDate, toDate, tujuanFilter, txnTypeFilter]);

  const summaryText = useMemo(() => {
    const base = `Menampilkan ${filteredHistory.length} order`;
    const qq = String(debouncedQ || "").trim();
    const extra = [];
    if (exactDate) extra.push(`tanggal: ${exactDate}`);
    if (!exactDate && fromDate) extra.push(`dari: ${fromDate}`);
    if (!exactDate && toDate) extra.push(`sampai: ${toDate}`);
    if (tujuanFilter !== "ALL") extra.push(`tujuan: ${tujuanFilter}`);
    if (txnTypeFilter === "ECOMMERCE") extra.push("tipe: e-commerce");
    if (txnTypeFilter === "MANUAL") extra.push("tipe: manual");
    const suffix = extra.length > 0 ? ` | ${extra.join(" • ")}` : "";
    return qq ? `${base} (pencarian: "${qq}")${suffix}` : `${base}${suffix}`;
  }, [filteredHistory.length, debouncedQ, exactDate, fromDate, toDate, tujuanFilter, txnTypeFilter]);

  const filteredTotals = useMemo(() => {
    return filteredHistory.reduce(
      (acc, order) => {
        const t = computeTotals(order);
        acc.orders += 1;
        acc.lines += t.totalLines;
        acc.qty += t.totalQty;
        return acc;
      },
      { orders: 0, lines: 0, qty: 0 }
    );
  }, [filteredHistory, computeTotals]);

  useEffect(() => {
    const urlQ = String(searchParams.get("q") || "");
    if (urlQ !== q) setQ(urlQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const currentQ = String(searchParams.get("q") || "");
    if (currentQ === debouncedQ) return;
    const next = new URLSearchParams(searchParams);
    if (debouncedQ) next.set("q", debouncedQ);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  }, [debouncedQ, searchParams, setSearchParams]);

  useEffect(() => {
    if (loading) return;

    const needAutoScroll = String(searchParams.get("autoscroll") || "") === "1";
    if (!needAutoScroll) return;

    const focusFromParam = Math.trunc(Number(searchParams.get("focusOrderId")));
    const targetId = Number.isInteger(focusFromParam) && focusFromParam > 0
      ? focusFromParam
      : Number(filteredHistory?.[0]?.id) || 0;

    if (!targetId) return;

    const el = cardRefs.current.get(targetId);
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightOrderId(targetId);

    const t = setTimeout(() => setHighlightOrderId(0), 1400);

    const next = new URLSearchParams(searchParams);
    next.delete("autoscroll");
    next.delete("focusOrderId");
    setSearchParams(next, { replace: true });

    return () => clearTimeout(t);
  }, [filteredHistory, loading, searchParams, setSearchParams]);

  const handleExportCsv = useCallback(async () => {
    try {
      setExporting(true);
      const qq = String(debouncedQ || "").trim();
      const path = `/api/orders/export${qq ? `?q=${encodeURIComponent(qq)}` : ""}`;

      const res = await api.get(path, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `history-orders-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      dialog.toast({ type: "success", title: "Export", message: "CSV History berhasil diunduh." });
    } catch (e) {
      dialog.toast({
        type: "error",
        title: "Export",
        message: e?.response?.data?.message || e.message || "Gagal export CSV History.",
      });
    } finally {
      setExporting(false);
    }
  }, [debouncedQ, dialog]);

  return (
    <div className="space-y-6 animate-fade-in pb-10 max-w-[1400px]">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Histori Transaksi</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Riwayat order POS lengkap dengan detail produk, bundle, bonus, dan pencatatan resi/refund.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard icon={ReceiptText} title="Order Tampil" value={filteredTotals.orders} />
        <KpiCard icon={ListOrdered} title="Total Baris" value={filteredTotals.lines} />
        <KpiCard icon={PackageOpen} title="Total Qty" value={filteredTotals.qty} />
      </div>

      <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 text-[13px] font-semibold text-zinc-900">
            <SlidersHorizontal className="h-4 w-4 text-zinc-400" />
            Filter Pencarian
          </div>
          <div className="text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 px-3 py-1.5 rounded-md self-start">
            {summaryText}
          </div>
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={handleExportCsv}
              disabled={exporting || loading}
              className="inline-flex items-center justify-center rounded-md border border-zinc-200 bg-white px-3 py-2 text-[13px] font-medium text-zinc-900 shadow-sm hover:bg-zinc-50 focus:outline-none focus:ring-1 focus:ring-zinc-900 disabled:opacity-50"
            >
              <FileText className="mr-2 h-4 w-4 text-zinc-500" />
              {exporting ? "Mengekspor..." : "Export CSV"}
            </button>
            <div className="w-full sm:w-72">
              <SearchBar value={q} onChange={setQ} placeholder="Cari nomor pesanan / resi…" />
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5 border-t border-zinc-100 pt-5">
          <div>
            <label className="mb-1.5 block text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Tanggal Tepat</label>
            <input
              type="date"
              value={exactDate}
              onChange={(e) => setExactDate(e.target.value)}
              className="w-full rounded-md border border-zinc-300 px-3 py-1.5 text-[13px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Mulai Dari</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              disabled={Boolean(exactDate)}
              className="w-full rounded-md border border-zinc-300 px-3 py-1.5 text-[13px] text-zinc-900 disabled:bg-zinc-50 disabled:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Sampai Dengan</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              disabled={Boolean(exactDate)}
              className="w-full rounded-md border border-zinc-300 px-3 py-1.5 text-[13px] text-zinc-900 disabled:bg-zinc-50 disabled:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Tujuan</label>
            <select
              value={tujuanFilter}
              onChange={(e) => setTujuanFilter(e.target.value)}
              className="w-full rounded-md border border-zinc-300 px-3 py-1.5 text-[13px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm bg-white"
            >
              {tujuanOptions.map((opt) => (
                <option key={opt} value={opt}>{opt === "ALL" ? "Semua Tujuan" : opt}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Tipe Transaksi</label>
            <select
              value={txnTypeFilter}
              onChange={(e) => setTxnTypeFilter(e.target.value)}
              className="w-full rounded-md border border-zinc-300 px-3 py-1.5 text-[13px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm bg-white"
            >
              <option value="ALL">Semua</option>
              <option value="ECOMMERCE">E-Commerce</option>
              <option value="MANUAL">Manual</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md text-[13px] font-medium text-zinc-500 hover:text-zinc-900 transition-colors"
            onClick={() => {
              setExactDate("");
              setFromDate("");
              setToDate("");
              setTujuanFilter("ALL");
              setTxnTypeFilter("ALL");
            }}
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Reset Filter
          </button>
        </div>
      </section>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-[13px] text-red-600">
          {error}
        </div>
      ) : null}

      {loading && filteredHistory.length === 0 ? (
        <div className="rounded-lg border border-zinc-200 bg-white p-12 text-center text-[13px] text-zinc-500 shadow-sm">
          Memuat history...
        </div>
      ) : filteredHistory.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-12 text-center shadow-sm">
          <ReceiptText className="h-8 w-8 text-zinc-300 mb-3" />
          <div className="text-[13px] font-medium text-zinc-900">Tidak ada order ditemukan</div>
          <div className="text-[11px] text-zinc-500 mt-1">Coba sesuaikan filter atau kata kunci pencarian Anda.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredHistory.map((order) => (
            <div
              key={order.id}
              ref={(el) => {
                if (el) cardRefs.current.set(Number(order.id), el);
                else cardRefs.current.delete(Number(order.id));
              }}
              className={
                "rounded-lg border border-zinc-200 bg-white shadow-sm transition-all duration-300 " +
                (Number(highlightOrderId) === Number(order.id)
                  ? "ring-2 ring-zinc-900/10 shadow-md scale-[1.005]"
                  : "hover:border-zinc-300 hover:shadow-md")
              }
            >
              <div className="border-b border-zinc-100 p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <div className="text-sm font-semibold text-zinc-900">{order.order_number || order.order_code || `Order #${order.id}`}</div>
                      <span className="inline-flex items-center rounded-md bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-600">
                        {formatDateTime(order.created_at) || "-"}
                      </span>
                    </div>
                    
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
                      {order.username ? <span className="inline-flex items-center rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-zinc-600">Oleh: <span className="font-medium text-zinc-900 ml-1">{order.username}</span></span> : null}
                      <span className="inline-flex items-center rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-zinc-600">Resi: <span className="font-mono font-medium text-zinc-900 ml-1">{order.resi_number || "-"}</span></span>
                      {order.tujuan ? (
                        <span className={"inline-flex items-center rounded-md px-2 py-1 font-medium ring-1 ring-inset " + toneBadgeClass("tujuan", order.tujuan)}>
                          {order.tujuan}
                        </span>
                      ) : null}
                      {order.source ? (
                        <span className={"inline-flex items-center rounded-md px-2 py-1 font-medium ring-1 ring-inset " + toneBadgeClass("source", order.source)}>
                          Source: {order.source}
                        </span>
                      ) : null}
                      {order.bundle_id ? (
                        <span className="inline-flex items-center rounded-md border border-zinc-200 bg-indigo-50/50 text-indigo-700 px-2 py-1 font-medium">
                          Bundle: {order.bundle_name || `#${order.bundle_id}`}
                        </span>
                      ) : null}
                    </div>

                    {order.keterangan ? (
                      <div className="mt-4 rounded-md bg-zinc-50 px-3 py-2 text-[13px] text-zinc-700 border border-zinc-100">
                        <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1">Keterangan</div>
                        <div className="whitespace-pre-wrap break-words leading-relaxed">{order.keterangan}</div>
                      </div>
                    ) : null}
                  </div>

                  <div className="shrink-0 sm:text-right flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2">
                    <div className="hidden sm:block">
                      <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">ID</div>
                      <div className="mt-0.5 font-mono text-[13px] font-semibold text-zinc-900">#{order.id}</div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => openEditResi(order)}
                        className="inline-flex items-center justify-center rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 focus:outline-none focus:ring-1 focus:ring-zinc-900 transition-colors"
                      >
                        <PencilLine className="mr-1.5 h-3.5 w-3.5 text-zinc-500" />
                        Edit Resi
                      </button>

                      {auth.isAdmin ? (
                        <button
                          type="button"
                          onClick={() => openRefund(order)}
                          className="inline-flex items-center justify-center rounded-md bg-rose-600 px-2.5 py-1.5 text-[11px] font-medium text-white shadow-sm hover:bg-rose-700 focus:outline-none focus:ring-1 focus:ring-rose-900 transition-colors"
                        >
                          Refund
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-5 bg-zinc-50/30">
                <div className="space-y-2">
                  <ReceiptTable title="PRODUK UTAMA" rows={order.items?.PRODUCT || []} />
                  <ReceiptTable
                    title={order.bundle_id ? `BUNDLE: ${order.bundle_name || `#${order.bundle_id}`}` : "BUNDLE"}
                    rows={order.items?.BOM || []}
                  />
                  <ReceiptTable title="BONUS" rows={order.items?.BONUS || []} />
                </div>

                {(() => {
                  const t = computeTotals(order);
                  return (
                    <div className="mt-5 flex items-center justify-between border-t border-zinc-200 pt-4">
                      <div className="flex items-center gap-1.5 text-[13px] font-medium text-zinc-500">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        Total Transaksi
                      </div>
                      <div className="flex items-center gap-3 text-[13px]">
                        <span className="text-zinc-600">Baris: <span className="font-semibold text-zinc-900">{t.totalLines}</span></span>
                        <span className="text-zinc-300">|</span>
                        <span className="text-zinc-600">Qty: <span className="font-semibold text-zinc-900">{t.totalQty}</span></span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

function KpiCard({ title, value, icon: Icon }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm flex flex-col justify-between group hover:border-zinc-300 transition-colors">
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-medium text-zinc-500 group-hover:text-zinc-700 transition-colors">{title}</div>
        {Icon && <Icon className="h-4 w-4 text-zinc-400 group-hover:text-zinc-600 transition-colors" strokeWidth={2} />}
      </div>
      <div className="mt-3 text-3xl font-semibold tracking-tight text-zinc-900 tabular-nums">{value}</div>
    </div>
  );
}

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), Math.max(0, Number(delayMs) || 0));
    return () => clearTimeout(t);
  }, [value, delayMs]);

  return debounced;
}

export default History;
